import { useEffect, useState } from 'react';
import { formatDate } from '@brimo/shared';
import {
  Banner,
  Button,
  Dialog,
  RadioCard,
  Skeleton,
  Textarea,
  Tooltip,
  useToast,
} from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import { requestRelease, useInvalidateReservations, useReservation, type ReservationItem } from './api';
import { useReservationActions } from './useReservationActions';
import styles from './ReleaseReservationDialog.module.css';

export interface ReleaseReservationDialogProps {
  open: boolean;
  reservationId: string;
  onClose: () => void;
  /** Called with the released reservation (Reservierung 10) or after „Aufhebung beantragen" (11, `null`). */
  onReleased: (item: ReservationItem | null) => void;
}

const REASON_REQUIRED = 'Bitte wählen Sie einen Grund.';
const REQUEST_REASON_REQUIRED = 'Bitte geben Sie einen Grund für den Antrag an.';

/** Whether the deal stands at or beyond „Reserviert" (then a release moves it back to „Beratung", R6). */
function dealAtOrBeyondReserved(systemKey: string | null): boolean {
  return systemKey !== null && systemKey !== 'consultation' && systemKey !== 'self_disclosure_done';
}

/**
 * Reservierung 10 „Reservierung aufheben" (reason radio cards, note, the three consequences, undo toast)
 * and Reservierung 11 for a foreign reservation without the right: the disabled button with the API's
 * tooltip and „Aufhebung beantragen" → `POST /reservations/:id/release-request`.
 */
export function ReleaseReservationDialog({
  open,
  reservationId,
  onClose,
  onReleased,
}: ReleaseReservationDialogProps) {
  const workspace = useWorkspace();
  const { toast } = useToast();
  const invalidate = useInvalidateReservations();
  const query = useReservation(open ? reservationId : null);
  const actions = useReservationActions(reservationId);
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason(null);
    setNote('');
    setRequestReason('');
    setError(null);
  }, [open, reservationId]);

  const item = query.data;
  const approver = workspace.approvalRecipientName ?? 'ein Admin';
  const mine =
    item !== undefined && (item.startedBy.id === workspace.user.id || item.advisor?.id === workspace.user.id);

  async function release() {
    if (!item) return;
    if (reason === null) {
      setError(REASON_REQUIRED);
      return;
    }
    setError(null);
    const body: { reason: string; note?: string } = { reason };
    if (note.trim()) body.note = note.trim();
    const released = await actions.release(body);
    if (released) onReleased(released);
  }

  async function request() {
    if (!item) return;
    if (requestReason.trim() === '') {
      setError(REQUEST_REASON_REQUIRED);
      return;
    }
    setError(null);
    setRequesting(true);
    try {
      await requestRelease(item.id, requestReason.trim());
      await invalidate();
      toast({ message: `Aufhebung beantragt · ${approver} prüft die Reservierung ${item.unit.number}` });
      onReleased(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : errorMessage(cause));
    } finally {
      setRequesting(false);
    }
  }

  const releasable = item !== undefined && (item.status === 'awaiting_approval' || item.status === 'active');
  const pending = actions.pending === 'release' || requesting;

  if (item && !item.canRelease) {
    // Reservierung 11 — no right to release a foreign reservation
    const openRequest = item.releaseRequest;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        width={560}
        eyebrow="Reservierung aufheben"
        title={`${item.unit.number} · ${item.contact.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Schließen
            </Button>
            <Tooltip content={item.releaseBlockedHelper ?? 'Dafür fehlt Ihnen die Berechtigung.'}>
              <span className={styles.disabledAnchor} tabIndex={0}>
                <Button variant="destructiveFilled" disabled>
                  Reservierung aufheben
                </Button>
              </span>
            </Tooltip>
          </>
        }
      >
        <div className={styles.stack}>
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <div className={styles.hintBox}>
            Diese Reservierung hat {item.startedBy.name} gestartet. Fremde Reservierungen heben nur Admins
            auf.
          </div>
          {openRequest ? (
            <Banner tone="info" title="Antrag auf Aufhebung offen">
              {openRequest.requestedByShort ?? 'Ein Nutzer'} hat die Aufhebung am {formatDate(openRequest.at)}{' '}
              beantragt{openRequest.reason ? ` · „${openRequest.reason}“` : ''}. {approver} entscheidet.
            </Banner>
          ) : item.canRequestRelease ? (
            <>
              <Textarea
                label="Grund für den Antrag"
                required
                rows={3}
                value={requestReason}
                onChange={(event) => setRequestReason(event.target.value)}
                error={error === REQUEST_REASON_REQUIRED ? error : undefined}
              />
              <div className={styles.requestRow}>
                <span className={styles.requestText}>Der Antrag erscheint als Freigabe bei {approver}.</span>
                <Button variant="secondary" loading={requesting} onClick={() => void request()}>
                  Aufhebung beantragen
                </Button>
              </div>
            </>
          ) : (
            <Banner tone="neutral">Diese Reservierung kann derzeit nicht aufgehoben werden.</Banner>
          )}
        </div>
      </Dialog>
    );
  }

  // Reservierung 10
  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow="Reservierung aufheben"
      title={item ? `${item.unit.number} für ${item.contact.name} freigeben?` : 'Reservierung aufheben'}
      subtitle={
        item
          ? mine
            ? 'Sie haben diese Reservierung gestartet und können sie jederzeit aufheben.'
            : `Diese Reservierung hat ${item.startedBy.name} gestartet. Sie dürfen fremde Reservierungen aufheben.`
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            variant="destructiveFilled"
            loading={actions.pending === 'release'}
            disabled={!item || !releasable}
            onClick={() => void release()}
          >
            Reservierung aufheben
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        {query.isError ? <Banner tone="danger">{errorMessage(query.error)}</Banner> : null}
        {query.isPending ? (
          <div className={styles.stack} aria-busy="true">
            <Skeleton height={44} />
            <Skeleton height={44} />
            <Skeleton height={92} />
          </div>
        ) : null}
        {item ? (
          <>
            {!releasable ? (
              <Banner tone="warning">
                Diese Reservierung ist {item.statusLabel.toLowerCase()} und kann nicht aufgehoben werden.
              </Banner>
            ) : null}
            <div className={styles.fieldLabel}>
              Grund <span className={styles.required}>*</span>
            </div>
            <div className={styles.reasons} role="radiogroup" aria-label="Grund">
              {item.options.releaseReasons.map((option) => (
                <RadioCard
                  key={option}
                  name="release-reason"
                  value={option}
                  checked={reason === option}
                  onChange={() => {
                    setReason(option);
                    setError(null);
                  }}
                  title={option}
                />
              ))}
            </div>
            {error ? (
              <div className={styles.fieldError} role="alert">
                {error}
              </div>
            ) : null}
            <Textarea
              label="Notiz"
              optional
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            <div className={styles.notes}>
              <span>{item.unit.number} wird sofort frei und ist für alle wieder reservierbar.</span>
              <span>
                {dealAtOrBeyondReserved(item.deal.stageSystemKey)
                  ? 'Der Deal bleibt bestehen und springt auf Stage „Beratung“ zurück.'
                  : `Der Deal bleibt bestehen und bleibt in Stage „${item.deal.stageName}“.`}
              </span>
              <span>
                {item.contact.name} erhält eine Bestätigung per E-Mail. 30 Sekunden lang rückgängig machbar.
              </span>
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
